// =============================================================================
// DESIGN STUDIO — Templates Email Métier
// JJ 2026-05-20 : déplacement des templates email "métier" (invitation
// installateur, invitation user) depuis Gouvernance → Design Studio.
// Les templates Système (alertes Gamora, AG-04…) restent en Gouvernance.
//
// Stockage : même collection `email_templates` (tag `owner: "design_studio"`).
// API : /api/governance/email-config/templates?owner=design_studio + PUT/POST.
// Avantage : routes auto qui envoient les emails par `key` (resend-installateur,
// invitation OmniHub) restent inchangées — pas de risque de régression.
// =============================================================================
import { useState, useEffect } from "react";
import {
  Mail, Edit, Save, X, Loader2, AlertCircle, Send, Eye, Plus, Sparkles,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import { getCurrentTenantSlug } from "../../../lib/utils";
import EmailRichEditor, { DEFAULT_EMAIL_VARIABLES } from "../../../components/editors/EmailRichEditor";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Valeurs expéditeur par défaut par tenant (pré-remplissage à la création).
// OMV : adresse de test à domaine vérifié Mandrill (cf. sender omv-france-cee).
const TENANT_SENDER_DEFAULTS = {
  omv_france: { from_name: "OMV France", from_email: "contact@omnihub-ia.fr" },
};

// Clé technique snake_case ASCII dérivée d'un nom lisible (ex. "Envoi devis" → "envoi_devis").
function slugifyKey(name) {
  return (name || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // retire accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

// Détecte les variables {{cle}} présentes dans le sujet + le corps.
function extractVariables(...texts) {
  const found = new Set();
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  for (const t of texts) {
    let m;
    while ((m = re.exec(t || "")) !== null) found.add(m[1]);
  }
  return [...found];
}

// Substitution {{cle}} → valeur pour l'aperçu client (sans appel backend).
function substituteVars(text, vars) {
  return (text || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) =>
    vars[k] != null && vars[k] !== "" ? vars[k] : `{{${k}}}`);
}

function getHeaders() {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function DesignStudioEmailTemplates({ currentUser = null }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [previewVars, setPreviewVars] = useState({});
  const [previewResult, setPreviewResult] = useState(null);

  // --- Création d'un nouveau template (WYSIWYG) ---
  // createForm === null → dialog de création fermé.
  const [createForm, setCreateForm] = useState(null);
  const [createPreviewOn, setCreatePreviewOn] = useState(false);
  const [createPreviewVars, setCreatePreviewVars] = useState({});
  const tenantSlug = getCurrentTenantSlug();

  // Note : on n'évalue plus canEdit côté UI — le backend
  // (governance.email_config_routes._require_admin) rejette les non-admins.
  // Affichage cohérent même si DesignStudio.jsx ne passe pas currentUser.
  const canEdit = true;

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/governance/email-config/templates?owner=design_studio`,
        { headers: getHeaders(), credentials: "include" },
      );
      if (res.ok) {
        setTemplates((await res.json()).templates || []);
      } else {
        toast.error("Erreur de chargement des templates");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTemplates(); }, []);

  // Bibliothèque de variables RÉELLE (≈132 système + perso tenant, 14 thèmes) au
  // lieu des 7 codées en dur. Endpoint déjà tenant-scopé (X-Tenant-ID). Fallback
  // sur DEFAULT_EMAIL_VARIABLES si le fetch échoue (offline / 4xx).
  const [emailVariables, setEmailVariables] = useState(DEFAULT_EMAIL_VARIABLES);
  useEffect(() => {
    const slug = getCurrentTenantSlug();
    (async () => {
      try {
        const res = await fetch(`${API}/api/metier/documents-engine/admin/variables`, {
          headers: { ...getHeaders(), "X-Tenant-ID": slug },
          credentials: "include",
        });
        if (!res.ok) return; // garde le fallback
        const data = await res.json();
        const all = [...(data.variables_systeme || []), ...(data.variables_personnalisees || [])];
        const mapped = all
          .filter((v) => v && v.nom)
          .map((v) => ({ key: v.nom, label: v.description || v.nom, theme: v.source || null }));
        if (mapped.length) setEmailVariables(mapped);
      } catch { /* fallback DEFAULT_EMAIL_VARIABLES */ }
    })();
  }, []);

  const openCreate = () => {
    const defaults = TENANT_SENDER_DEFAULTS[tenantSlug] || { from_name: "", from_email: "" };
    setCreateForm({
      name: "",
      subject: "",
      from_name: defaults.from_name,
      from_email: defaults.from_email,
      html_body: "",
      text_body: "",
      category: "marketing",
    });
    setCreatePreviewOn(false);
    setCreatePreviewVars({});
  };

  const computedKey = createForm ? slugifyKey(createForm.name) : "";

  const saveCreate = async () => {
    if (!createForm) return;
    const key = slugifyKey(createForm.name);
    if (!key) { toast.error("Donnez un nom au template"); return; }
    if (!createForm.subject.trim()) { toast.error("Le sujet est requis"); return; }
    setSaving(true);
    try {
      const variables = extractVariables(createForm.subject, createForm.html_body)
        .map((k) => ({ key: k, required: false }));
      const payload = {
        key,
        // tenant_id forcé au tenant de session côté backend pour un non-super-admin
        // (anti-IDOR) ; on l'envoie quand même pour le cas super_admin.
        tenant_id: tenantSlug,
        owner: "design_studio", // sinon invisible dans la liste filtrée owner=design_studio
        category: createForm.category || "marketing",
        name: createForm.name,
        subject: createForm.subject,
        from_name: createForm.from_name || null,
        from_email: createForm.from_email || null,
        html_body: createForm.html_body || "",
        text_body: createForm.text_body || "",
        variables,
        active: true,
      };
      const res = await fetch(
        `${API}/api/governance/email-config/templates`,
        {
          method: "POST",
          headers: { ...getHeaders(), "X-Tenant-ID": tenantSlug },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );
      if (res.ok) {
        toast.success("Template créé");
        setCreateForm(null);
        await loadTemplates();
      } else if (res.status === 409) {
        toast.error("Un template porte déjà ce nom (clé existante). Changez le nom.");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur (HTTP ${res.status})`);
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const toggleCreatePreview = () => {
    const vars = extractVariables(createForm.subject, createForm.html_body);
    const seed = {};
    vars.forEach((k) => { seed[k] = createPreviewVars[k] ?? ""; });
    setCreatePreviewVars(seed);
    setCreatePreviewOn((v) => !v);
  };

  const startEdit = (tpl) => {
    setEditing({ ...tpl });
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { key, tenant_id, ...payload } = editing;
      const qs = tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : "";
      const res = await fetch(
        `${API}/api/governance/email-config/templates/${encodeURIComponent(key)}${qs}`,
        {
          method: "PUT",
          headers: getHeaders(),
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );
      if (res.ok) {
        toast.success("Template mis à jour");
        setEditing(null);
        await loadTemplates();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur (HTTP ${res.status})`);
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = async () => {
    if (!previewing) return;
    try {
      const { key, tenant_id } = previewing;
      const qs = tenant_id ? `?tenant_id=${encodeURIComponent(tenant_id)}` : "";
      const res = await fetch(
        `${API}/api/governance/email-config/templates/${encodeURIComponent(key)}/preview${qs}`,
        {
          method: "POST",
          headers: getHeaders(),
          credentials: "include",
          body: JSON.stringify({ variables: previewVars }),
        },
      );
      if (res.ok) {
        setPreviewResult(await res.json());
      } else {
        toast.error("Erreur preview");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm" style={{ color: "var(--oh-text-muted)" }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="design-studio-email-templates">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
          Templates email métier
        </p>
        <Button size="sm" onClick={openCreate} data-testid="ds-tpl-new">
          <Plus className="w-4 h-4 mr-1" /> Nouveau template
        </Button>
      </div>

      <div className="flex items-start gap-2 p-3 rounded text-xs"
           style={{ background: "var(--oh-stat-cyan-bg)", color: "var(--oh-stat-cyan-text)", border: "1px solid var(--oh-stat-cyan-border)" }}>
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Templates métier déplacés depuis Gouvernance (JJ 2026-05-20).
          Modifications sauvegardées en direct. Routes auto envoyant les
          emails (resend invitation installateur, invitation utilisateur
          OmniHub) inchangées — utilisent toujours <code>key</code>.
        </div>
      </div>

      {templates.length === 0 ? (
        <p className="text-sm p-4" style={{ color: "var(--oh-text-muted)" }}>
          Aucun template métier pour ce compte. Cliquez sur
          «&nbsp;Nouveau template&nbsp;» pour en créer un (éditeur visuel,
          sans HTML).
        </p>
      ) : (
        <div className="space-y-2">
          {templates.map((tpl) => (
            <div
              key={`${tpl.key}-${tpl.tenant_id || "global"}`}
              className="p-3 rounded flex items-center justify-between gap-3"
              style={{
                background: "var(--oh-bg-surface)",
                border: "1px solid var(--oh-border)",
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 shrink-0" style={{ color: "var(--oh-stat-purple-text)" }} />
                  <span className="font-semibold text-sm" style={{ color: "var(--oh-text)" }}>
                    {tpl.name || tpl.key}
                  </span>
                  <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/30 border">
                    Métier
                  </Badge>
                  {tpl.tenant_id && (
                    <Badge variant="outline" className="text-xs">
                      {tpl.tenant_id}
                    </Badge>
                  )}
                  {!tpl.active && (
                    <Badge variant="outline" className="text-xs text-red-400 border-red-500/30">
                      Inactif
                    </Badge>
                  )}
                </div>
                <div className="text-xs space-y-0.5" style={{ color: "var(--oh-text-muted)" }}>
                  <div><span className="font-mono">{tpl.key}</span></div>
                  <div className="truncate"><strong>Sujet :</strong> {tpl.subject}</div>
                  {tpl.from_name && (
                    <div><strong>Expéditeur :</strong> {tpl.from_name} {tpl.from_email && `<${tpl.from_email}>`}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setPreviewing(tpl); setPreviewVars({}); setPreviewResult(null); }}
                  data-testid={`ds-tpl-preview-${tpl.key}`}
                >
                  <Eye className="w-3 h-3 mr-1" /> Aperçu
                </Button>
                {canEdit && (
                  <Button
                    size="sm"
                    onClick={() => startEdit(tpl)}
                    data-testid={`ds-tpl-edit-${tpl.key}`}
                  >
                    <Edit className="w-3 h-3 mr-1" /> Modifier
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* === Edit Modal === */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && cancelEdit()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier : {editing?.name || editing?.key}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Clé technique (lecture seule)</Label>
                  <Input value={editing.key} disabled className="font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Nom affiché</Label>
                  <Input
                    value={editing.name || ""}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Sujet (peut contenir {`{{variable}}`})</Label>
                <Input
                  value={editing.subject || ""}
                  onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                  data-testid="ds-tpl-edit-subject"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Expéditeur (nom)</Label>
                  <Input
                    value={editing.from_name || ""}
                    onChange={(e) => setEditing({ ...editing, from_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Expéditeur (email)</Label>
                  <Input
                    value={editing.from_email || ""}
                    onChange={(e) => setEditing({ ...editing, from_email: e.target.value })}
                    placeholder="invitations@exemple.invalid"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Corps de l'email</Label>
                {/* MÊME éditeur WYSIWYG qu'à la création (TipTap) — charge le HTML
                    existant dans l'éditeur riche (plus de textarea brut). */}
                <EmailRichEditor
                  value={editing.html_body || ""}
                  onChange={(html) => setEditing({ ...editing, html_body: html })}
                  variables={emailVariables}
                  tenantId={tenantSlug}
                  data-testid="ds-tpl-edit-html"
                />
              </div>
              <div>
                <Label className="text-xs">Corps texte (fallback)</Label>
                <Textarea
                  value={editing.text_body || ""}
                  onChange={(e) => setEditing({ ...editing, text_body: e.target.value })}
                  rows={6}
                  className="font-mono text-xs"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={cancelEdit} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="ds-tpl-edit-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Preview Modal === */}
      <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu : {previewing?.name || previewing?.key}</DialogTitle>
          </DialogHeader>
          {previewing && (
            <div className="space-y-3">
              {(previewing.variables || []).length > 0 && (
                <div>
                  <Label className="text-xs">Variables (JSON)</Label>
                  <Textarea
                    placeholder={JSON.stringify(
                      Object.fromEntries((previewing.variables || []).map(v => [v.key, v.default ?? `<${v.key}>`])),
                      null, 2
                    )}
                    onChange={(e) => {
                      try { setPreviewVars(JSON.parse(e.target.value)); } catch { /* invalid JSON */ }
                    }}
                    rows={4}
                    className="font-mono text-xs"
                  />
                </div>
              )}
              <Button size="sm" onClick={renderPreview}>
                <Send className="w-3 h-3 mr-1" /> Générer aperçu
              </Button>
              {previewResult && (
                <div className="space-y-2 mt-3 p-3 rounded" style={{ background: "var(--oh-bg-base)", border: "1px solid var(--oh-border)" }}>
                  <div><strong>Sujet rendu :</strong> {previewResult.subject}</div>
                  <div>
                    <strong>HTML :</strong>
                    <div
                      className="mt-1 p-2 rounded text-xs"
                      style={{ background: "white", color: "black" }}
                      dangerouslySetInnerHTML={{ __html: previewResult.html || previewResult.html_body || "" }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === Création d'un nouveau template (WYSIWYG) === */}
      <Dialog open={!!createForm} onOpenChange={(o) => !o && setCreateForm(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: "var(--oh-stat-purple-text)" }} />
              Nouveau template email
            </DialogTitle>
          </DialogHeader>
          {createForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nom du template</Label>
                  <Input
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Ex : Envoi devis"
                    data-testid="ds-tpl-new-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Clé technique (générée)</Label>
                  <Input value={computedKey} disabled className="font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">
                  Sujet (peut contenir une variable {"{{...}}"})
                </Label>
                <Input
                  value={createForm.subject}
                  onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
                  placeholder="Votre devis est disponible"
                  data-testid="ds-tpl-new-subject"
                />
              </div>
              <div>
                <Label className="text-xs">Catégorie</Label>
                <select
                  value={createForm.category || "marketing"}
                  onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                  data-testid="ds-tpl-new-category"
                  className="w-full px-2 py-1.5 text-sm rounded"
                  style={{ background: "var(--oh-bg-2)", color: "var(--oh-text)", border: "1px solid var(--oh-border)" }}
                >
                  <option value="devis">Devis (proposé à l'envoi d'un devis)</option>
                  <option value="marketing">Marketing</option>
                  <option value="notification">Notification</option>
                  <option value="onboarding">Onboarding / invitation</option>
                  <option value="alert">Alerte</option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  « Devis » = seuls ces templates apparaissent dans le sélecteur d'envoi de devis.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Expéditeur (nom)</Label>
                  <Input
                    value={createForm.from_name || ""}
                    onChange={(e) => setCreateForm({ ...createForm, from_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Expéditeur (email)</Label>
                  <Input
                    value={createForm.from_email || ""}
                    onChange={(e) => setCreateForm({ ...createForm, from_email: e.target.value })}
                    placeholder="contact@exemple.invalid"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Corps de l'email</Label>
                <EmailRichEditor
                  value={createForm.html_body}
                  onChange={(html) => setCreateForm((f) => (f ? { ...f, html_body: html } : f))}
                  variables={emailVariables}
                  tenantId={tenantSlug}
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={toggleCreatePreview} data-testid="ds-tpl-new-preview">
                  <Eye className="w-3 h-3 mr-1" />
                  {createPreviewOn ? "Masquer l'aperçu" : "Aperçu"}
                </Button>
                <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                  Variables&nbsp;:{" "}
                  {extractVariables(createForm.subject, createForm.html_body).join(", ") || "aucune"}
                </span>
              </div>

              {createPreviewOn && (
                <div className="space-y-2 p-3 rounded" style={{ background: "var(--oh-bg-base)", border: "1px solid var(--oh-border)" }}>
                  {Object.keys(createPreviewVars).length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {Object.keys(createPreviewVars).map((k) => (
                        <div key={k}>
                          <Label className="text-[10px] font-mono">{k}</Label>
                          <Input
                            className="h-7 text-xs"
                            value={createPreviewVars[k]}
                            onChange={(e) => setCreatePreviewVars({ ...createPreviewVars, [k]: e.target.value })}
                            placeholder={`<${k}>`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                    <strong>Sujet :</strong> {substituteVars(createForm.subject, createPreviewVars)}
                  </div>
                  <div
                    className="rounded p-3 text-sm"
                    style={{ background: "white", color: "black" }}
                    dangerouslySetInnerHTML={{
                      __html: substituteVars(createForm.html_body, createPreviewVars) || "<em>(corps vide)</em>",
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateForm(null)} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={saveCreate} disabled={saving} data-testid="ds-tpl-new-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Créer le template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
